import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { deleteShortcut, fetchShortcuts, saveShortcut } from "../api/shortcuts.ts";
import { useAppActions } from "../app/AppProvider.tsx";
import { useModal } from "../components/ModalProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { TextShortcut } from "../types.ts";
import {
  canDeleteShortcut,
  expandShortcutText,
  normalizeShortcutTrigger,
  shortcutSearchText,
} from "./Shortcuts.helpers.ts";
import { ShortcutEditor } from "./shortcuts/ShortcutEditor.tsx";
import { ShortcutList } from "./shortcuts/ShortcutList.tsx";
import { shortcutToForm, sortShortcuts, upsertShortcut } from "./shortcuts/formatters.ts";
import { createEmptyShortcutForm, type ShortcutFormState } from "./shortcuts/types.ts";

export function Shortcuts() {
  const { state, patch } = useAppActions();
  const { showToast } = useToast();
  const modal = useModal();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<ShortcutFormState>(() => createEmptyShortcutForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const shortcuts = state.shortcuts || [];
  const role = state.user?.role || "";
  const isAdmin = role === "admin";
  const editing = form.id ? shortcuts.find((item) => String(item.id) === form.id) || null : null;
  const activeChat = state.activeChatJid ? state.chats.find((chat) => chat.chatJid === state.activeChatJid) || null : null;
  const activeLead = activeChat?.leadId
    ? state.leads.find((lead) => String(lead.id) === String(activeChat.leadId)) || null
    : null;

  useEffect(() => {
    if (!state.authToken) return;
    let alive = true;
    setLoading(true);
    fetchShortcuts()
      .then((items) => {
        if (alive) patch({ shortcuts: sortShortcuts(items) });
      })
      .catch((err: unknown) => {
        if (alive) showToast(err instanceof Error ? err.message : "Falha ao carregar atalhos", "error");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [state.authToken, patch, showToast]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return sortShortcuts(shortcuts);
    return sortShortcuts(shortcuts).filter((item) => shortcutSearchText(item).includes(search));
  }, [shortcuts, query]);

  const previewText = useMemo(() => {
    if (!form.body) return "O texto expandido aparece aqui.";
    return expandShortcutText(form.body, { lead: activeLead, chat: activeChat });
  }, [activeChat, activeLead, form.body]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trigger = normalizeShortcutTrigger(form.trigger);
    const body = form.body.trim();
    if (trigger.length < 2 || trigger === "/") {
      showToast("Informe um gatilho, exemplo: /pix", "error");
      return;
    }
    if (!body) {
      showToast("Informe o texto do atalho", "error");
      return;
    }

    const scope = isAdmin ? form.scope : "personal";
    setSaving(true);
    try {
      const saved = await saveShortcut({
        trigger,
        title: form.title.trim() || trigger,
        body,
        scope,
      });
      let next = upsertShortcut(shortcuts, saved);
      const oldKey = editing ? `${editing.scope}:${normalizeShortcutTrigger(editing.trigger)}` : "";
      const newKey = `${saved.scope}:${normalizeShortcutTrigger(saved.trigger)}`;
      if (editing && String(editing.id) !== String(saved.id) && oldKey !== newKey && canDeleteShortcut(editing, role)) {
        await deleteShortcut(editing.id);
        next = next.filter((item) => String(item.id) !== String(editing.id));
      }
      patch({ shortcuts: sortShortcuts(next) });
      setForm(shortcutToForm(saved));
      showToast("Atalho salvo", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao salvar atalho", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(shortcut: TextShortcut) {
    if (!canDeleteShortcut(shortcut, role)) {
      showToast("Atalho global so pode ser removido por admin", "error");
      return;
    }
    const decision = await modal.open({
      kind: "danger",
      title: "Remover atalho",
      message: `Remover ${shortcut.trigger}? Essa acao nao pode ser desfeita.`,
      destructive: true,
      actions: [
        { label: "Cancelar", value: "cancel", variant: "secondary" },
        { label: "Remover", value: "delete", variant: "danger" },
      ],
    });
    if (decision !== "delete") return;
    try {
      await deleteShortcut(shortcut.id);
      patch({ shortcuts: shortcuts.filter((item) => String(item.id) !== String(shortcut.id)) });
      if (form.id === String(shortcut.id)) setForm(createEmptyShortcutForm());
      showToast("Atalho removido", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao remover atalho", "error");
    }
  }

  function handleNewShortcut() {
    setForm(createEmptyShortcutForm());
  }

  function handleInsertVariable(token: string) {
    const target = bodyRef.current;
    const current = form.body || "";
    const start = target?.selectionStart ?? current.length;
    const end = target?.selectionEnd ?? current.length;
    const before = current.slice(0, start);
    const after = current.slice(end);
    const spacerBefore = before && !before.endsWith(" ") && !before.endsWith("\n") ? " " : "";
    const spacerAfter = after && !after.startsWith(" ") && !after.startsWith("\n") ? " " : "";
    const nextBody = `${before}${spacerBefore}${token}${spacerAfter}${after}`;
    const cursor = before.length + spacerBefore.length + token.length;
    setForm((prev) => ({ ...prev, body: nextBody }));
    requestAnimationFrame(() => {
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(cursor, cursor);
    });
  }

  function handleInsertIntoActiveChat(shortcut: TextShortcut) {
    if (!activeChat) {
      showToast("Abra uma conversa para inserir o atalho", "error");
      return;
    }
    const expanded = expandShortcutText(shortcut.body, { lead: activeLead, chat: activeChat });
    patch({ panel: "chats", composer: { chatJid: activeChat.chatJid, text: expanded } });
    showToast("Atalho inserido na conversa", "success");
  }

  async function handleCopyShortcut(shortcut: TextShortcut) {
    const text = expandShortcutText(shortcut.body, { lead: activeLead, chat: activeChat });
    try {
      await navigator.clipboard.writeText(text);
      showToast("Texto copiado", "success");
    } catch {
      showToast("Nao foi possivel copiar automaticamente", "error");
    }
  }

  return (
    <main className="main main-shortcuts shortcuts-main">
      <section className="shortcuts-shell">
        <header className="shortcuts-head">
          <div>
            <h1>Atalhos</h1>
            <p>Crie respostas reutilizáveis e use gatilhos como <strong>/pix</strong> dentro das conversas.</p>
          </div>
          <button className="btn btn-primary" type="button" onClick={handleNewShortcut}>
            Novo atalho
          </button>
        </header>

        <section className="shortcuts-grid">
          <ShortcutList
            shortcuts={filtered}
            activeShortcutId={form.id}
            loading={loading}
            query={query}
            role={role}
            canInsert={!!activeChat}
            onQueryChange={setQuery}
            onEdit={(shortcut) => setForm(shortcutToForm(shortcut))}
            onDelete={(shortcut) => void handleDelete(shortcut)}
            onInsert={handleInsertIntoActiveChat}
            onCopy={(shortcut) => void handleCopyShortcut(shortcut)}
          />

          <ShortcutEditor
            form={form}
            editing={editing}
            bodyRef={bodyRef}
            isAdmin={isAdmin}
            saving={saving}
            previewText={previewText}
            onFormChange={setForm}
            onInsertVariable={handleInsertVariable}
            onNew={handleNewShortcut}
            onSubmit={(event) => void handleSubmit(event)}
          />
        </section>
      </section>
    </main>
  );
}
